import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Search, X } from "lucide-react";
import { useAttachments, type AttachmentFilter } from "@/api/queries/attachments";
import { useBulkDeleteAttachments } from "@/api/mutations/attachments";
import { useAgents } from "@/api/queries/agents";
import { useChannels } from "@/api/queries/channels";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ListTableShell } from "@/components/ResourceShell";
import { relativeTime } from "@/lib/relativeTime";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const PAGE_SIZE = 50;

const MIME_OPTIONS: Array<{ label: string; value: string }> = [
  { label: "Alle Typen", value: "" },
  { label: "Bilder", value: "image/" },
  { label: "Video", value: "video/" },
  { label: "Audio", value: "audio/" },
  { label: "PDF", value: "application/pdf" },
  { label: "Dokumente", value: "application/" },
];

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

const selectCls =
  "h-9 rounded-lg border border-border-strong bg-surface px-2.5 text-[13px] text-text outline-none focus:border-accent-border";

export function AttachmentsList() {
  const [filename, setFilename] = useState("");
  const [direction, setDirection] = useState<"" | "inbound" | "outbound">("");
  const [mimetypePrefix, setMimetypePrefix] = useState("");
  const [ownerAgentId, setOwnerAgentId] = useState("");
  const [channelId, setChannelId] = useState("");
  const [createdAfter, setCreatedAfter] = useState("");
  const [createdBefore, setCreatedBefore] = useState("");
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);

  const filter: AttachmentFilter = {
    filenameContains: filename.trim() || undefined,
    direction: direction || undefined,
    mimetypePrefix: mimetypePrefix || undefined,
    ownerAgentId: ownerAgentId.trim() || undefined,
    channelId: channelId.trim() || undefined,
    createdAfter: createdAfter ? new Date(createdAfter).toISOString() : undefined,
    createdBefore: createdBefore ? new Date(`${createdBefore}T23:59:59`).toISOString() : undefined,
    limit: PAGE_SIZE,
    offset,
  };

  const { data, isLoading, isError, error, refetch } = useAttachments(filter);
  const bulkDelete = useBulkDeleteAttachments();
  const agentMap = new Map((useAgents().data ?? []).map((a) => [a.id, a]));
  const channelMap = new Map((useChannels().data ?? []).map((c) => [c.id, c]));
  const rows = data?.items ?? [];
  const total = data?.total ?? 0;

  function onFilterChange<T>(setter: (v: T) => void) {
    return (v: T) => { setter(v); setOffset(0); setSelected(new Set()); };
  }
  function toggle(id: string) {
    setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }
  function toggleAll() {
    setSelected((s) => (s.size === rows.length ? new Set() : new Set(rows.map((r) => r.id))));
  }
  function resetFilters() {
    setFilename(""); setDirection(""); setMimetypePrefix(""); setOwnerAgentId("");
    setChannelId(""); setCreatedAfter(""); setCreatedBefore(""); setOffset(0); setSelected(new Set());
  }
  function handleBulkDelete() {
    bulkDelete.mutate([...selected], {
      onSuccess: (r) => {
        toast.success(`${r.deleted} Attachment(s) gelöscht`);
        setSelected(new Set());
        setConfirmOpen(false);
      },
      onError: (e) => toast.error(`Löschen fehlgeschlagen: ${(e as Error).message}`),
    });
  }

  const allChecked = rows.length > 0 && selected.size === rows.length;

  return (
    <div className="space-y-4">
      <p className="max-w-[640px] text-[13px] text-text3">
        Hochgeladene Dateien (Inbound + Outbound). Filtern, mehrere auswählen, löschen. Kein Auto-Cleanup.
      </p>

      {/* Bulk-Leiste ersetzt die Filter, sobald etwas ausgewählt ist (wie Agents-Liste). */}
      {selected.size > 0 ? (
        <div className="flex items-center gap-3 rounded-sm border border-accent-border bg-accent-soft px-3.5 py-2.5" data-testid="bulk-bar">
          <span className="text-[13px] font-bold text-accent-text">{selected.size} ausgewählt</span>
          <Button size="sm" variant="destructive" onClick={() => setConfirmOpen(true)} data-testid="bulk-delete">
            <Trash2 className="h-4 w-4" />Löschen
          </Button>
          <div className="flex-1" />
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
            <X className="h-4 w-4" />Auswahl aufheben
          </Button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2" data-testid="attachment-filters">
          <div className="relative min-w-[180px] max-w-[260px] flex-1">
            <Search className="pointer-events-none absolute left-[11px] top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-text3" />
            <input
              value={filename}
              onChange={(e) => onFilterChange(setFilename)(e.target.value)}
              placeholder="Dateiname suchen…"
              data-testid="filter-filename"
              className="h-9 w-full rounded-lg border border-border-strong bg-surface pl-[34px] pr-3 text-[13px] text-text outline-none placeholder:text-text3 focus:border-accent-border"
            />
          </div>
          <select className={selectCls} value={direction} onChange={(e) => onFilterChange(setDirection)(e.target.value as "" | "inbound" | "outbound")} data-testid="filter-direction">
            <option value="">Alle Richtungen</option>
            <option value="inbound">Inbound (rein)</option>
            <option value="outbound">Outbound (raus)</option>
          </select>
          <select className={selectCls} value={mimetypePrefix} onChange={(e) => onFilterChange(setMimetypePrefix)(e.target.value)} data-testid="filter-mimetype">
            {MIME_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <input placeholder="Agent-ID" value={ownerAgentId} onChange={(e) => onFilterChange(setOwnerAgentId)(e.target.value)} className={`${selectCls} w-36 placeholder:text-text3`} data-testid="filter-agent" />
          <input placeholder="Channel-ID" value={channelId} onChange={(e) => onFilterChange(setChannelId)(e.target.value)} className={`${selectCls} w-36 placeholder:text-text3`} data-testid="filter-channel" />
          <input type="date" className={selectCls} value={createdAfter} onChange={(e) => onFilterChange(setCreatedAfter)(e.target.value)} title="ab Datum" />
          <input type="date" className={selectCls} value={createdBefore} onChange={(e) => onFilterChange(setCreatedBefore)(e.target.value)} title="bis Datum" />
          <Button variant="outline" size="sm" onClick={resetFilters}>Filter zurücksetzen</Button>
        </div>
      )}

      <ListTableShell
        isLoading={isLoading && !data}
        isError={isError}
        errorMessage={(error as Error | undefined)?.message}
        isEmpty={!isLoading && rows.length === 0}
        emptyMessage="Keine Attachments (für diesen Filter)"
        onRetry={() => refetch()}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-9 pr-0">
                <input type="checkbox" checked={allChecked} onChange={toggleAll} aria-label="Alle auswählen" data-testid="select-all" className="h-[15px] w-[15px] accent-accent" />
              </TableHead>
              <TableHead>Datei</TableHead>
              <TableHead>Typ</TableHead>
              <TableHead className="text-right">Größe</TableHead>
              <TableHead>Richtung</TableHead>
              <TableHead>Agent</TableHead>
              <TableHead>Channel</TableHead>
              <TableHead className="text-right">Erstellt</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((a) => {
              const sel = selected.has(a.id);
              const agent = agentMap.get(a.ownerAgentId);
              const channel = a.channelId ? channelMap.get(a.channelId) : null;
              return (
                <TableRow key={a.id} className={sel ? "bg-accent-soft hover:bg-accent-soft" : undefined}>
                  <TableCell className="pr-0">
                    <input type="checkbox" checked={sel} onChange={() => toggle(a.id)} aria-label={`${a.filename} auswählen`} className="h-[15px] w-[15px] accent-accent" />
                  </TableCell>
                  <TableCell className="max-w-[220px] truncate font-medium" title={a.filename}>{a.filename}</TableCell>
                  <TableCell><span className="mono text-[11px] text-text3">{a.mimetype.split("/")[1] || a.mimetype}</span></TableCell>
                  <TableCell className="text-right"><span className="tnum text-[12.5px] text-text2">{formatBytes(a.size)}</span></TableCell>
                  <TableCell>
                    <Badge variant={a.direction === "inbound" ? "enrolled" : "default"}>{a.direction === "inbound" ? "⬇ in" : "⬆ out"}</Badge>
                  </TableCell>
                  <TableCell>
                    <Link to={`/agents/${a.ownerAgentId}`} className="text-[12.5px] font-semibold text-accent-text hover:underline">
                      {agent?.name ?? `${a.ownerAgentId.slice(0, 8)}…`}
                    </Link>
                  </TableCell>
                  <TableCell>
                    {channel ? <span className="mono text-[11.5px] text-text3">{channel.name}</span> : <span className="text-text3">—</span>}
                  </TableCell>
                  <TableCell className="text-right"><span className="text-[12px] text-text3">{relativeTime(a.createdAt)}</span></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </ListTableShell>

      {/* Pagination */}
      <div className="flex items-center justify-between text-[12.5px] text-text3">
        <span data-testid="attachment-total">{total} gesamt{total > 0 ? ` · zeige ${offset + 1}–${Math.min(offset + PAGE_SIZE, total)}` : ""}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => { setOffset(Math.max(0, offset - PAGE_SIZE)); setSelected(new Set()); }}>Zurück</Button>
          <Button variant="outline" size="sm" disabled={offset + PAGE_SIZE >= total} onClick={() => { setOffset(offset + PAGE_SIZE); setSelected(new Set()); }}>Weiter</Button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Attachments löschen"
        description={`${selected.size} Attachment(s) endgültig löschen — Bytes + Metadaten. Nicht umkehrbar.`}
        confirmText="Endgültig löschen"
        confirmVariant="destructive"
        isPending={bulkDelete.isPending}
        onConfirm={handleBulkDelete}
      />
    </div>
  );
}
